import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Fab,
  Dialog,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  useMediaQuery,
} from '@mui/material';
import {
  Person,
  Business,
  Map,
  Search,
  Favorite,
  FavoriteBorder,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import EditInvestorProfile from '../modals/EditInvestorProfile';
import PropertyMap from '../components/PropertyMap';
import { formatPropertyAddress, propertyImageAlt } from '../utils/propertyDisplay';

export default function InvestorLanding() {
  const { userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [savedProperties, setSavedProperties] = useState([]);
  const [savedPropertyIds, setSavedPropertyIds] = useState(() => new Set());
  const [mapOpen, setMapOpen] = useState(false);
  const [editProfileModalOpen, setEditProfileModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    bedrooms: 'all',
    propertyType: 'all',
  });

  // Handle map modal close with cleanup
  const handleMapClose = () => {
    setMapOpen(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const [propertiesResponse, savedResponse] = await Promise.all([
          api.properties.getAll(),
          api.properties.getSavedByInvestor(userProfile?.uid)
        ]);
        console.log('[InvestorLanding] properties.getAll ->', propertiesResponse.data?.length);
        setProperties(propertiesResponse.data); // Show all properties
        setSavedProperties(savedResponse.data || []);
        setSavedPropertyIds(new Set((savedResponse.data || []).map((p) => p.id)));
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoadError(error?.message || 'Failed to load properties');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userProfile?.uid]);

  const handleToggleSaved = async (property) => {
    try {
      const isSaved = savedPropertyIds.has(property.id);
      const result = await api.properties.toggleSaved(property.id, {
        investorName: userProfile?.displayName || userProfile?.email || null
      });

      const nextSaved = result?.data?.saved ?? !isSaved;
      setSavedPropertyIds((prev) => {
        const next = new Set(prev);
        if (nextSaved) next.add(property.id);
        else next.delete(property.id);
        return next;
      });

      // Refresh saved list (keeps it consistent with visibility changes / removals)
      const savedResponse = await api.properties.getSavedByInvestor(userProfile?.uid);
      setSavedProperties(savedResponse.data || []);
      setSavedPropertyIds(new Set((savedResponse.data || []).map((p) => p.id)));
    } catch (err) {
      console.error('Failed to toggle saved property:', err);
    }
  };

  // Apply filters whenever properties or filters change
  useEffect(() => {
    let filtered = [...properties];

    // Search filter
    if (filters.search) {
      const q = filters.search.toLowerCase();
      filtered = filtered.filter(property =>
        formatPropertyAddress(property, '').toLowerCase().includes(q) ||
        property.description?.toLowerCase().includes(q)
      );
    }

    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(property => normalizeStatus(property.status) === filters.status);
    }

    // Bedrooms filter
    if (filters.bedrooms !== 'all') {
      if (filters.bedrooms === 'studio') {
        filtered = filtered.filter(property => property.bedrooms === 0 || !property.bedrooms);
      } else {
        const bedroomCount = parseInt(filters.bedrooms);
        filtered = filtered.filter(property => property.bedrooms === bedroomCount);
      }
    }

    // Property type filter
    if (filters.propertyType !== 'all') {
      filtered = filtered.filter(property => property.propertyType === filters.propertyType);
    }

    setFilteredProperties(filtered);
  }, [properties, filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Helper function to normalize status values
  const normalizeStatus = (status) => {
    if (!status) return 'available';
    const statusMap = {
      'Available': 'available',
      'available': 'available',
      'On-Hold': 'on-hold',
      'on-hold': 'on-hold',
      'First Come First Serve': 'first come first serve',
      'first come first serve': 'first come first serve',
      'Vacant on Lockbox': 'vacant on lockbox',
      'vacant on lockbox': 'vacant on lockbox',
      'Under Contract': 'under contract',
      'under contract': 'under contract',
      'Pending': 'pending',
      'pending': 'pending',
      'Sold': 'sold',
      'sold': 'sold'
    };
    return statusMap[status] || 'available';
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ backgroundColor: '#008080', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 2, md: 3 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 3 }, width: '100%' }}>
              <Avatar
                src={userProfile?.profilePhoto}
                sx={{
                  width: { xs: 56, sm: 72, md: 80 },
                  height: { xs: 56, sm: 72, md: 80 },
                  border: '3px solid white',
                  boxShadow: 2
                }}
              >
                <Person sx={{ fontSize: 40 }} />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.125rem' } }}>
                  Welcome back, {userProfile?.displayName || userProfile?.email}!
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' } }}>
                  Access exclusive off-market investment opportunities
                </Typography>
              </Box>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', md: 'auto' } }}>
              <Button
                variant="outlined"
                onClick={() => setEditProfileModalOpen(true)}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                }}
                fullWidth={isMobile}
              >
                Edit Profile
              </Button>
              <Button
                variant="outlined"
                onClick={handleLogout}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                }}
                fullWidth={isMobile}
              >
                Logout
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {loadError && (
        <Container maxWidth="lg" sx={{ mt: 2 }}>
          <Alert severity="error">
            {loadError}
          </Alert>
        </Container>
      )}

      {/* Main Layout Container */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: 'calc(100vh - 140px)' }}>
        {/* Saved Properties - Left Sidebar */}
                          <Box
                            sx={{
            width: { xs: '100%', md: '320px' },
            backgroundColor: 'white',
            borderRight: { xs: 'none', md: '1px solid #e0e0e0' },
            borderBottom: { xs: '1px solid #e0e0e0', md: 'none' },
            p: { xs: 2, md: 3 }
          }}
        >
                <Typography variant="h6" gutterBottom sx={{ color: '#008080', display: 'flex', alignItems: 'center' }}>
                  <Favorite sx={{ mr: 1 }} />
                  Saved Properties
                </Typography>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress sx={{ color: '#008080' }} size={24} />
                  </Box>
                ) : (
                  <Stack spacing={2}>
                    {savedProperties.map((p) => (
                      <Card
                        key={p.id}
                        variant="outlined"
                        sx={{ p: 2, cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 128, 128, 0.04)' } }}
                        onClick={() => navigate(`/property/${p.id}`)}
                      >
                        <Stack spacing={0.5}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {formatPropertyAddress(p)}
                          </Typography>
                          {p.propertyType && (
                            <Typography variant="caption" color="text.secondary">
                              {p.propertyType}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ color: '#008080', fontWeight: 600 }}>
                            {Number.isFinite(p.price) ? `$${p.price.toLocaleString()}` : 'N/A'}
                          </Typography>
                        </Stack>
                      </Card>
                    ))}
                    {savedProperties.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        You have no saved properties.
                      </Typography>
                    )}
                  </Stack>
                )}
        </Box>

        {/* Main Content Area */}
        <Box sx={{ flex: 1, backgroundColor: '#f8f9fa', p: { xs: 2, md: 3 } }}>
          <Container maxWidth="xl">
          {/* Featured Properties */}
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Business sx={{ mr: 1 }} />
                    Featured Off-Market Properties ({filteredProperties.length})
                </Typography>
                </Box>

                {/* Filters */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      placeholder="Search properties..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={filters.status}
                        label="Status"
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                      >
                        <MenuItem value="all">All Status</MenuItem>
                        <MenuItem value="available">Available</MenuItem>
                        <MenuItem value="on-hold">On-Hold</MenuItem>
                        <MenuItem value="first come first serve">First Come First Serve</MenuItem>
                        <MenuItem value="vacant on lockbox">Vacant on Lockbox</MenuItem>
                        <MenuItem value="under contract">Under Contract</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="sold">Sold</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Bedrooms</InputLabel>
                      <Select
                        value={filters.bedrooms}
                        label="Bedrooms"
                        onChange={(e) => handleFilterChange('bedrooms', e.target.value)}
                      >
                        <MenuItem value="all">All Bedrooms</MenuItem>
                        <MenuItem value="studio">Studio</MenuItem>
                        <MenuItem value="1">1 Bedroom</MenuItem>
                        <MenuItem value="2">2 Bedrooms</MenuItem>
                        <MenuItem value="3">3 Bedrooms</MenuItem>
                        <MenuItem value="4">4 Bedrooms</MenuItem>
                        <MenuItem value="5">5+ Bedrooms</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Property Type</InputLabel>
                      <Select
                        value={filters.propertyType}
                        label="Property Type"
                        onChange={(e) => handleFilterChange('propertyType', e.target.value)}
                      >
                        <MenuItem value="all">All Types</MenuItem>
                        <MenuItem value="single-family">Single Family</MenuItem>
                        <MenuItem value="multi-family">Multi Family</MenuItem>
                        <MenuItem value="condo">Condo</MenuItem>
                        <MenuItem value="townhouse">Townhouse</MenuItem>
                        <MenuItem value="duplex">Duplex</MenuItem>
                        <MenuItem value="commercial">Commercial</MenuItem>
                        <MenuItem value="land">Land</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Properties Table */}
                {isMobile ? (
                  <Stack spacing={2}>
                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress sx={{ color: '#008080' }} />
                      </Box>
                    ) : filteredProperties.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No properties found matching your filters.
                      </Typography>
                    ) : (
                      filteredProperties.map((property) => (
                        <Card key={property.id} variant="outlined" sx={{ p: 2 }}>
                          <Stack spacing={1.25}>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                              <Box
                                sx={{
                                  width: 64,
                                  height: 48,
                                  backgroundColor: 'grey.200',
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  overflow: 'hidden',
                                  flexShrink: 0,
                                }}
                              >
                                {property.images && property.images.length > 0 ? (
                                  <Box
                                    component="img"
                                    src={property.images[0]}
                                    alt={propertyImageAlt(property)}
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <Business sx={{ fontSize: 24, color: 'grey.400' }} />
                                )}
                              </Box>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                                  {formatPropertyAddress(property)}
                                </Typography>
                              </Box>
                              <IconButton
                                onClick={() => handleToggleSaved(property)}
                                aria-label={savedPropertyIds.has(property.id) ? 'Unsave property' : 'Save property'}
                                sx={{ color: savedPropertyIds.has(property.id) ? '#FF6B6B' : 'grey.500' }}
                              >
                                {savedPropertyIds.has(property.id) ? <Favorite /> : <FavoriteBorder />}
                              </IconButton>
                            </Box>

                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                              {property.bedrooms != null && (
                                <Chip label={`${property.bedrooms}BR`} size="small" variant="outlined" />
                              )}
                              {property.bathrooms != null && (
                                <Chip label={`${property.bathrooms}BA`} size="small" variant="outlined" />
                              )}
                              {property.squareFootage && (
                                <Chip label={`${property.squareFootage.toLocaleString()} sqft`} size="small" variant="outlined" />
                              )}
                              {property.propertyType && (
                                <Chip label={property.propertyType} size="small" variant="outlined" />
                              )}
                            </Stack>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#008080' }}>
                                  {Number.isFinite(property.price) ? `$${property.price.toLocaleString()}` : 'N/A'}
                                </Typography>
                              </Box>
                              <Chip
                                label={property.status || 'available'}
                                size="small"
                                sx={{
                                  backgroundColor:
                                    normalizeStatus(property.status) === 'available' ? '#28a745' :
                                    normalizeStatus(property.status) === 'on-hold' ? '#dc3545' :
                                    normalizeStatus(property.status) === 'first come first serve' ? '#17a2b8' :
                                    normalizeStatus(property.status) === 'vacant on lockbox' ? '#008080' :
                                    normalizeStatus(property.status) === 'under contract' ? '#ffc107' :
                                    normalizeStatus(property.status) === 'pending' ? '#6f42c1' :
                                    normalizeStatus(property.status) === 'sold' ? '#fd7e14' :
                                    '#6c757d',
                                  color: 'white'
                                }}
                              />
                            </Box>

                            <Stack direction="row" spacing={1}>
                              <Button
                                variant="outlined"
                                size="small"
                                fullWidth
                                onClick={() => navigate(`/property/${property.id}`)}
                                sx={{
                                  borderColor: '#008080',
                                  color: '#008080',
                                  '&:hover': { borderColor: '#005252', backgroundColor: 'rgba(0, 128, 128, 0.04)' }
                                }}
                              >
                                View Details
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                fullWidth
                                onClick={() => window.open(`/vendor/${property.vendorId}`, '_blank')}
                                sx={{
                                  backgroundColor: '#FFA500',
                                  color: '#000000',
                                  '&:hover': { backgroundColor: '#CC8400', color: '#000000' }
                                }}
                              >
                                View Vendor
                              </Button>
                            </Stack>
                          </Stack>
                        </Card>
                      ))
                    )}
                  </Stack>
                ) : (
                  <TableContainer component={Paper} sx={{ boxShadow: 2, overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                          <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Property</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Details</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Price</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '20%', textAlign: 'center' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                              <CircularProgress sx={{ color: '#008080' }} />
                            </TableCell>
                          </TableRow>
                        ) : filteredProperties.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                              <Typography variant="body1" color="text.secondary">
                                No properties found matching your filters.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredProperties.map((property) => (
                            <TableRow key={property.id} hover sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box
                                    sx={{
                                      width: 80,
                                      height: 60,
                                      backgroundColor: 'grey.200',
                                      borderRadius: 1,
                                      mr: 2,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      overflow: 'hidden'
                                    }}
                                  >
                                    {property.images && property.images.length > 0 ? (
                                      <Box
                                        component="img"
                                        src={property.images[0]}
                                        alt={propertyImageAlt(property)}
                                        sx={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover'
                                        }}
                                      />
                                    ) : (
                                      <Business sx={{ fontSize: 24, color: 'grey.400' }} />
                                    )}
                                  </Box>
                                  <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                      {formatPropertyAddress(property)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Stack spacing={0.5}>
                                  {property.bedrooms && (
                                    <Typography variant="body2">
                                      {property.bedrooms}BR
                                    </Typography>
                                  )}
                                  {property.bathrooms && (
                                    <Typography variant="body2">
                                      {property.bathrooms}BA
                                    </Typography>
                                  )}
                                  {property.squareFootage && (
                                    <Typography variant="body2">
                                      {property.squareFootage.toLocaleString()} sqft
                                    </Typography>
                                  )}
                                  <Chip
                                    label={property.propertyType}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: '20px', mt: 0.5 }}
                                  />
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body1" sx={{ fontWeight: 500, color: '#008080' }}>
                                  {Number.isFinite(property.price) ? `$${property.price.toLocaleString()}` : 'N/A'}
                                </Typography>
                                {property.hoaFee && (
                                  <Typography variant="body2" color="text.secondary">
                                    HOA: ${property.hoaFee}/mo
                                  </Typography>
                                )}
                                {property.spaceRent && (
                                  <Typography variant="body2" color="text.secondary">
                                    Space Rent: ${property.spaceRent}/sq ft/yr
                                  </Typography>
                                )}
                                {property.closeOfEscrow && (
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    Close: {new Date(property.closeOfEscrow).toLocaleDateString()}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={property.status || 'available'}
                                  size="small"
                                  sx={{
                                    backgroundColor:
                                      normalizeStatus(property.status) === 'available' ? '#28a745' :
                                      normalizeStatus(property.status) === 'on-hold' ? '#dc3545' :
                                      normalizeStatus(property.status) === 'first come first serve' ? '#17a2b8' :
                                      normalizeStatus(property.status) === 'vacant on lockbox' ? '#008080' :
                                      normalizeStatus(property.status) === 'under contract' ? '#ffc107' :
                                      normalizeStatus(property.status) === 'pending' ? '#6c757d' :
                                      normalizeStatus(property.status) === 'sold' ? '#fd7e14' :
                                      '#6c757d',
                                    color: 'white'
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                <Stack direction="column" spacing={1} alignItems="center">
                                  <IconButton
                                    onClick={() => handleToggleSaved(property)}
                                    aria-label={savedPropertyIds.has(property.id) ? 'Unsave property' : 'Save property'}
                                    sx={{ color: savedPropertyIds.has(property.id) ? '#FF6B6B' : 'grey.500' }}
                                  >
                                    {savedPropertyIds.has(property.id) ? <Favorite /> : <FavoriteBorder />}
                                  </IconButton>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => navigate(`/property/${property.id}`)}
                                    sx={{
                                      minWidth: 'auto',
                                      px: 2,
                                      borderColor: '#008080',
                                      color: '#008080',
                                      fontSize: '0.75rem',
                                      '&:hover': {
                                        borderColor: '#005252',
                                        backgroundColor: 'rgba(0, 128, 128, 0.04)'
                                      }
                                    }}
                                  >
                                    View Details
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => window.open(`/vendor/${property.vendorId}`, '_blank')}
                                    sx={{
                                      minWidth: 'auto',
                                      px: 2,
                                      borderColor: '#FFA500',
                                      color: '#000000',
                                      backgroundColor: '#FFA500',
                                      fontSize: '0.75rem',
                                      '&:hover': {
                                        borderColor: '#CC8400',
                                        backgroundColor: '#CC8400',
                                        color: '#000000'
                                      }
                                    }}
                                  >
                                    View Vendor
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      borderColor: '#008080',
                      color: '#008080',
                      '&:hover': {
                        borderColor: '#005252',
                        backgroundColor: 'rgba(0, 128, 128, 0.04)'
                      }
                    }}
                  >
                    View All Properties
                  </Button>
                </Box>
              </CardContent>
            </Card>
      </Container>
        </Box>
      </Box>

      <EditInvestorProfile
        open={editProfileModalOpen}
        onClose={() => setEditProfileModalOpen(false)}
      />

      {/* Map Overlay Dialog */}
      <Dialog
        open={mapOpen}
        onClose={handleMapClose}
        maxWidth="lg"
        fullWidth
        fullScreen
        PaperProps={{
          sx: {
            height: '100vh',
            maxHeight: '100vh',
          }
        }}
      >
        <DialogContent sx={{ p: 0, height: '100vh' }}>
          <PropertyMap
            properties={properties}
            onClose={handleMapClose}
            onViewProperty={(propertyId) => {
              navigate(`/property/${propertyId}`);
              handleMapClose();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Floating Action Button for Map */}
      {properties && properties.length > 0 && (
        <Fab
          color="primary"
          aria-label="open map"
          onClick={() => setMapOpen(true)}
          sx={{
            position: 'fixed',
            bottom: { xs: 16, md: 24 },
            right: { xs: 16, md: 24 },
            backgroundColor: '#008080',
            '&:hover': {
              backgroundColor: '#005252',
            },
          }}
        >
          <Map />
        </Fab>
      )}

    </Box>
  );
}
